import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Company } from '@/types'
import { companyApi } from '@/utils/api'

export const useCompanyStore = defineStore('company', () => {
  // State
  const company = ref<Company | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isLoaded = computed(() => !!company.value)
  const primaryColor = computed(() => company.value?.primary_color || '#FF6B35')
  const secondaryColor = computed(() => company.value?.secondary_color || '#004E89')
  const logoUrl = computed(() => company.value?.logo_url)
  const paymentMethods = computed(() => company.value?.payment_methods || [])
  const minOrderValue = computed(() => company.value?.min_order_value || 0)
  const averageDeliveryTime = computed(() => company.value?.average_delivery_time || 45)
  
  // URL do sistema (usar a configurada ou localhost como fallback)
  const systemUrl = computed(() => {
    return company.value?.system_url || 'http://localhost:5173'
  })

  // Domínios base suportados para subdomínios SaaS
  const SAAS_DOMAIN_BASES = ['deliveryapphub.com', 'deliveryapphub.com.br']

  // Actions
  const setCompany = (newCompany: Company | null) => {
    company.value = newCompany
    if (newCompany) {
      localStorage.setItem('company', JSON.stringify(newCompany))
      localStorage.setItem('company_id', newCompany.id.toString())
      if (newCompany.slug) {
        localStorage.setItem('company_slug', newCompany.slug)
      }
      if (newCompany.subdomain) {
        localStorage.setItem('company_subdomain', newCompany.subdomain)
      }
      if (newCompany.domain_base) {
        localStorage.setItem('company_domain_base', newCompany.domain_base)
      }
      if (newCompany.custom_domain_base) {
        localStorage.setItem('company_custom_domain_base', newCompany.custom_domain_base)
      }
      if (newCompany.custom_domain) {
        localStorage.setItem('company_custom_domain', newCompany.custom_domain)
      }
    }
  }

  const fetchBySlug = async (slug: string) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await companyApi.getBySlug(slug)
      setCompany(response.data)
      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Erro ao carregar empresa'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const fetchById = async (id: number) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await companyApi.getById(id)
      setCompany(response.data)
      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Erro ao carregar empresa'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const fetchBySubdomain = async (subdomain: string) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await companyApi.getBySubdomain(subdomain)
      setCompany(response.data)
      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Erro ao carregar empresa'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const fetchByDomain = async (domain: string) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await companyApi.getByDomain(domain)
      setCompany(response.data)
      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Erro ao carregar empresa'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const fetchByHostname = async (hostname: string) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await companyApi.getByHostname(hostname)
      setCompany(response.data)
      return { success: true }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Erro ao carregar empresa'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  /**
   * Detecta o hostname atual e retorna a empresa associada.
   * Resolve subdomínios dos domínios base padrão, subdomínios de domínio base próprio
   * e domínios próprios exatos.
   */
  const detectCompanyByHostname = () => {
    const hostname = window.location.hostname.toLowerCase().replace(/^www\./, '')
    
    // Se for localhost ou IP, retorna null
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null
    }
    
    return hostname
  }

  // Detecta o subdomínio atual (mantido para compatibilidade)
  const detectSubdomain = () => {
    const hostname = window.location.hostname.toLowerCase().replace(/^www\./, '')
    
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null
    }
    
    for (const base of SAAS_DOMAIN_BASES) {
      if (hostname.endsWith('.' + base)) {
        const subdomain = hostname.slice(0, -(base.length + 1))
        if (subdomain && subdomain !== 'www') {
          return subdomain
        }
      }
    }
    return null
  }

  const init = () => {
    // Tenta carregar empresa do localStorage
    const storedCompany = localStorage.getItem('company')
    if (storedCompany) {
      try {
        const parsed = JSON.parse(storedCompany)
        const currentHostname = detectCompanyByHostname()
        
        // Se há hostname detectado e é diferente do salvo, limpa o cache
        if (currentHostname) {
          const savedHostname = parsed.custom_domain
            || (parsed.subdomain && (parsed.custom_domain_base || parsed.domain_base))
          if (savedHostname && currentHostname !== savedHostname && !currentHostname.endsWith('.' + savedHostname)) {
            clear()
            return
          }
        }
        
        company.value = parsed
      } catch {
        localStorage.removeItem('company')
      }
    }
  }

  const clear = () => {
    company.value = null
    localStorage.removeItem('company')
    localStorage.removeItem('company_id')
    localStorage.removeItem('company_slug')
    localStorage.removeItem('company_subdomain')
    localStorage.removeItem('company_domain_base')
    localStorage.removeItem('company_custom_domain_base')
    localStorage.removeItem('company_custom_domain')
  }

  return {
    // State
    company,
    loading,
    error,
    // Getters
    isLoaded,
    primaryColor,
    secondaryColor,
    logoUrl,
    paymentMethods,
    minOrderValue,
    averageDeliveryTime,
    systemUrl,
    // Actions
    fetchBySlug,
    fetchById,
    fetchBySubdomain,
    fetchByDomain,
    fetchByHostname,
    detectCompanyByHostname,
    detectSubdomain,
    setCompany,
    init,
    clear
  }
})
